import { AnyAction } from 'redux';
import {
  AdressvorschlagFailedPayload,
  CALLER_ID,
  GlobalActions,
  ProvideVorhandeneVwgValidationPayload,
  RoleDeterminedPayload,
  StreckenTarifierungRequestEvents,
  VorbelegungPayload,
} from '@ps20-frontend/shop-integration-redux';
import { ownPricingErrorReducer, OwnPricingEvents } from '@ps20-frontend/reducers';
import {
  App,
  BaseTransientState,
  FeatureToggles,
  MapViewModal,
  MultiangebotAdressenValidationResult,
  Regexps,
  Rolle,
  VhvRelevantVorbelegungdaten,
  VhvVorbelegungdaten,
  VorvertragVorschaeden,
} from '@ps20-frontend/frontend-models';
import { VorschadenArt } from '@ps20/bff-models';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';

export interface TransientState extends BaseTransientState, Rolle {
  adressvorschlagIsInProgress: boolean;
  vhvVorhandenError: MultiangebotAdressenValidationResult;
  vorvertragVorschaeden?: VorvertragVorschaedenVhv;
  isRisikoortVnAdresse?: boolean;
  mapViewModal: MapViewModal;
  relevantVorbelegungdaten?: VhvRelevantVorbelegungdaten;
  formValidationTriggered?: boolean;
}

export interface VorvertragVorschaedenVhv extends Record<string, number> {
  fels: number;
  fahrrad: number;
  elementar: number;
}

const ownPricingEvents: OwnPricingEvents = {
  failed: StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VHV,
  successful: [
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VHV,
    StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VHV,
  ],
};

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
  appState: IAppState,
): TransientState {
  switch (action.type) {
    case VhvActions.ZURUECK_ZUM_SHOP_MODAL_OPEN: {
      return { ...sectionState, zurueckZumShopModalOpen: true };
    }
    case VhvActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE: {
      return { ...sectionState, zurueckZumShopModalOpen: false };
    }
    case GlobalActions.TARIFIERUNG_HAS_STARTED: {
      return { ...sectionState, tarifierungIsInProgress: true };
    }
    case GlobalActions.TARIFIERUNG_HAS_ENDED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case GlobalActions.TARIFIERUNG_CANCELED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return { ...sectionState, featureToggles: actionPayload };
    }
    case VhvActions.PLZ_CHANGED: {
      return {
        ...sectionState,
        adressvorschlagIsInProgress: true,
        mapViewModal: {
          ...sectionState.mapViewModal,
          isAdresseTarifierbar: true,
        },
      };
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL:
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      const angebotsId = appState.angebotsId;
      const callerId = `${CALLER_ID.VHV_BASISDATEN}_${angebotsId}`;
      if (payload.callerId !== callerId) {
        return sectionState;
      }
      return { ...sectionState, adressvorschlagIsInProgress: false };
    }
    case GlobalActions.DO_REQUEST:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_FAILED_VHV:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_VHV:
    case StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_VHV:
      return ownPricingErrorReducer(
        sectionState,
        action,
        { appId: App.VHV, angebotsId: appState?.angebotsId },
        ownPricingEvents,
      );
    case GlobalActions.SET_PRICE_GUARANTEE: {
      return { ...sectionState, priceGuarantee: action.payload };
    }
    case GlobalActions.CHANGE_RELEVANT_RISKDATA_HASH: {
      return {
        ...sectionState,
        priceGuarantee: false,
      };
    }
    case GlobalActions.PROVIDE_MULTIANGEBOT_ADRESS_VALIDATION: {
      const { appId, angebotsId, validationResult }: ProvideVorhandeneVwgValidationPayload =
        action.payload;
      if (appId === App.VHV && angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          vhvVorhandenError: validationResult,
        };
      }
      return sectionState;
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return { ...sectionState, role: actionPayload.role };
    }
    case GlobalActions.VORVERTRAG_VORSCHAEDEN_ERMITTELT: {
      const actionPayload: Array<VorvertragVorschaeden> = action.payload;
      const vorvertragVorschaeden = actionPayload.filter(
        vvVorschaeden =>
          vvVorschaeden.appId === App.VHV && vvVorschaeden.angebotsId === appState.angebotsId,
      );
      if (vorvertragVorschaeden?.length > 0) {
        const anzahlFels = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.FELS,
        )?.anzahlVorschaeden;
        const anzahlFahrrad = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.RAD,
        )?.anzahlVorschaeden;
        const anzahlElementar = vorvertragVorschaeden.find(
          vv => vv.vorschaedenArt === VorschadenArt.ELEMENTAR,
        )?.anzahlVorschaeden;
        return {
          ...sectionState,
          vorvertragVorschaeden: {
            fahrrad: anzahlFahrrad || 0,
            fels: anzahlFels || 0,
            elementar: anzahlElementar || 0,
          },
        };
      } else {
        return sectionState;
      }
    }
    case VhvActions.FORM_VALIDATION_TRIGGERED: {
      return {
        ...sectionState,
        formValidationTriggered: true,
      };
    }
    case GlobalActions.OPEN_MAP_VIEW_MODAL: {
      if (action.payload.appId === App.VHV && action.payload.angebotsId === appState.angebotsId) {
        return {
          ...sectionState,
          mapViewModal: {
            isOpen: true,
            isAdresseTarifierbar:
              action.payload.adresseTarifierbar ?? sectionState.mapViewModal.isAdresseTarifierbar,
            origin: action.payload.origin ? action.payload.origin : null,
          },
        };
      }
      return sectionState;
    }
    case GlobalActions.CLOSE_MAP_VIEW_MODAL: {
      return {
        ...sectionState,
        mapViewModal: {
          ...sectionState.mapViewModal,
          isOpen: false,
        },
      };
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      const actionPayload: VorbelegungPayload = action.payload;
      if (
        actionPayload.appId === App.VHV &&
        actionPayload.angebotsId === appState.angebotsId &&
        actionPayload.vorhandeneVorbelegungdaten
      ) {
        const vorhandeneVorbelegungdaten: VhvVorbelegungdaten =
          actionPayload.vorhandeneVorbelegungdaten as VhvVorbelegungdaten;
        return {
          ...sectionState,
          relevantVorbelegungdaten: {
            wohnflaeche: vorhandeneVorbelegungdaten?.wohnflaeche,
            verssumme: vorhandeneVorbelegungdaten?.versicherungssumme,
            vorvertragAdresse: vorhandeneVorbelegungdaten?.risikoort,
            wertsachensumme: vorhandeneVorbelegungdaten?.wertsachensumme,
          },
        };
      }
      return sectionState;
    }
    case GlobalActions.REGEXPS_RECEIVED: {
      const actionPayload: Regexps = action.payload;
      return { ...sectionState, regexps: actionPayload };
    }
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED: {
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    }
    default:
      return sectionState;
  }
};
